import { useEffect, useState } from 'react';
import { apiBaseUrl } from '../authConfig.js';
import { CostKpi, CountKpi, Panel, CohortBadge, ConfidenceBar, type KpiSpec } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { gbp, count, NO_DATA } from '../lib/format.js';

interface CommandCentreData {
  generatedUtc: string;
  entities: {
    uniqueContacts: number; uniqueCompanies: number; geDeals: number;
    historicalDeals: number; lifecycleEvents: number; revenueEvents: number;
  };
  cohort: { entity: string; cohort: string; n: number }[];
  funnel: { level: number; label: string; inCohort: number; preLaunch: number; events: number }[];
  attribution: { models: { model: string; known: number; likely: number; unknown: number; unattributed: number }[]; totalOutcomes: number };
  attributionHealth: {
    touchpoints: number; paidTouchpoints: number; withClickId: number; missingClickIds: number;
    contactsWithoutCompany: number; dealsWithoutContact: number; openWarnings: number; openErrors: number;
  };
  pipeline: {
    geActiveDeals: number; geActiveGbp: number | null; geDeferredDeals: number; geDeferredGbp: number | null;
    historical: { semantic_stage: string; n: number; gbp: number | null }[];
  };
  revenue: { components: { component_code: string; display_name: string; is_msp: boolean; gbp: number | null }[]; unclassifiedProducts: number };
  advertising: { isRunning: boolean; spendToDateGbp: number | null; clicks: number | null };
  enquiries: { count: number; latestUtc: string | null };
  cost: Record<string, KpiSpec>;
}

const MODEL_LABEL: Record<string, string> = {
  FIRST_TOUCH: 'First touch', LAST_TOUCH: 'Last touch', PAID_ACQUISITION: 'Paid acquisition',
};

export function CommandCentre({ getToken }: { getToken: () => Promise<string | null> }): JSX.Element {
  const [data, setData] = useState<CommandCentreData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const r = await fetch(`${apiBaseUrl}/api/command-centre`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) throw new Error(`API returned ${r.status}`);
        const body = (await r.json()) as CommandCentreData;
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Unknown error');
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  if (error) return <EmptyState title="Could not load the Command Centre" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading from the Growth Engine database.</EmptyState>;


  /*
    These four KPIs read `inCohortEvents === 0 ? null : null` — both branches
    null, so they showed NO DATA permanently, including after the first real
    lead arrived. The funnel already carries the per-level counts; they were
    simply never wired up.

    Zero is shown as ZERO, not NO DATA, once advertising is running. We are
    measuring: tracking is live, the forms are live, and we would know within
    seconds if a lead arrived. "No lead yet" is an observed fact and the number
    Tony is watching, not an absence of measurement.
  */
  const atLevel = (level: number): number | null => {
    const row = data.funnel.find((f) => f.level === level);
    if (!row) return null;
    return data.advertising.isRunning || row.inCohort > 0 ? row.inCohort : null;
  };
  const mqlCount = atLevel(6) ?? 0;
  const sqlCount = atLevel(7) ?? 0;
  const paidModel = data.attribution.models.find((m) => m.model === 'PAID_ACQUISITION');
  const attributedPct = data.attribution.totalOutcomes === 0 ? 0
    : Math.round((((paidModel?.known ?? 0) + (paidModel?.likely ?? 0)) / data.attribution.totalOutcomes) * 1000) / 10;

  return (
    <div className="stack">
      {/*
        This banner used to read "No advertising has run yet" whenever the
        cohort was empty. Those are two different facts, and since 24 September
        they have had two different answers: money IS being spent, and no lead
        has arrived yet. Saying the first when only the second is true tells
        Tony his campaigns are not running when they are.
      */}
      {data.advertising.isRunning && data.enquiries.count > 0 ? (
        <div className="panel" style={{ borderColor: 'var(--positive)' }}>
          <h2>{count(data.enquiries.count)} enquir{data.enquiries.count === 1 ? 'y' : 'ies'} from paid advertising</h2>
          <p>
            Advertising is live and has spent{' '}
            <strong>{data.advertising.spendToDateGbp === null ? NO_DATA : gbp(data.advertising.spendToDateGbp)}</strong>
            {data.advertising.clicks ? <> across <strong>{count(data.advertising.clicks)}</strong> clicks</> : null}
            .{' '}
            {data.enquiries.latestUtc
              ? <>The most recent enquiry was on <strong>{new Date(data.enquiries.latestUtc).toLocaleDateString('en-GB')}</strong>.{' '}</>
              : null}
            {mqlCount > 0
              ? <><strong>{count(mqlCount)}</strong> met the ICP test and {mqlCount === 1 ? 'is' : 'are'} recorded as MQL.{' '}</>
              : <>None has met the ICP test yet.{' '}</>}
            {sqlCount > 0
              ? <><strong>{count(sqlCount)}</strong> {sqlCount === 1 ? 'has' : 'have'} been qualified to SQL.</>
              : <>Nothing has reached SQL. That stage is set by Matt in HubSpot and is never written
                 by the platform, so an empty SQL means no lead has been qualified yet rather than a
                 measurement gap.</>}
          </p>
        </div>
      ) : null}

      {data.advertising.isRunning && data.enquiries.count === 0 ? (
        <div className="panel" style={{ borderColor: 'var(--warning)' }}>
          <h2>Campaigns are running — no leads yet</h2>
          <p>
            Advertising is live and has spent{' '}
            <strong>{data.advertising.spendToDateGbp === null ? NO_DATA
              : gbp(data.advertising.spendToDateGbp)}</strong>
            {data.advertising.clicks ? <> across <strong>{count(data.advertising.clicks)}</strong> clicks</> : null}.
            Spend and cost per click below are real. Everything from <strong>lead</strong> onwards still reads{' '}
            <strong>{NO_DATA}</strong> because no form has been submitted yet — that is a measured absence,
            not a zero, and it is the number to watch.
          </p>
        </div>
      ) : null}

      {!data.advertising.isRunning ? (
        <div className="panel" style={{ borderColor: 'var(--warning)' }}>
          <h2>No advertising has run yet</h2>
          <p>
            No campaign spend has been recorded, so every advertising performance figure below reads{' '}
            <strong>{NO_DATA}</strong> rather than zero. Everything shown is <strong>pre-launch historical
            context</strong> and is deliberately kept separate — it cannot contaminate campaign performance.
          </p>
        </div>
      ) : null}

      {/* ---- Commercial KPI hierarchy: spend through to revenue ---- */}
      <Panel title="Acquisition performance" subtitle="Growth Engine cohort only. Spend is measured; everything from lead onwards is NO DATA until a form is submitted.">
        <div className="kpi-grid">
          <CostKpi label="Spend MTD" kpi={data.cost.spendMtd!} />
          <CountKpi label="Leads" value={data.enquiries.count} note="Recorded form submissions" />
          <CountKpi label="MQL" value={atLevel(6)} note="Growth Engine cohort" />
          <CountKpi label="SQL" value={atLevel(7)} note="Growth Engine cohort" />
          <CountKpi label="Opportunities" value={data.pipeline.geActiveDeals || null} note="GE pipeline" />
          <CountKpi label="Active pipeline" value={data.pipeline.geActiveGbp} money note="Excludes deferred" />
          <CountKpi label="Deferred pipeline" value={data.pipeline.geDeferredGbp} money note="Not lost, not forecast" />
          <CountKpi label="MSP customers won" value={atLevel(10)} note="Managed-service contracts" />
          <CountKpi label="New MSP MRR" value={null} money note="Approved managed-service revenue only" />
          <CostKpi label="Cost per SQL" kpi={data.cost.costPerSql!} />
          <CostKpi label="Cost per opportunity" kpi={data.cost.costPerMql!} />
          <CostKpi label="CAC" kpi={data.cost.customerAcquisitionCost!} />
        </div>
      </Panel>

      {/* ---- Entities vs events ---- */}
      <Panel
        title="What we actually hold"
        subtitle="Entities and events are counted separately. A lifecycle event is not a prospect, and these figures must never be summed."
      >
        <div className="kpi-grid">
          <CountKpi label="Unique contacts" value={data.entities.uniqueContacts} />
          <CountKpi label="Unique companies" value={data.entities.uniqueCompanies} />
          <CountKpi label="GE deals" value={data.entities.geDeals} note="Authoritative pipeline" />
          <CountKpi label="Historical deals" value={data.entities.historicalDeals} note="Context only, excluded" />
          <CountKpi label="Lifecycle events" value={data.entities.lifecycleEvents} note="Events, not prospects" />
          <CountKpi label="Revenue events" value={data.entities.revenueEvents} />
        </div>
      </Panel>

      {/* ---- Funnel with cohort separation ---- */}
      <Panel title="Funnel" subtitle="Distinct subjects per stage, split by cohort. Pre-launch is shown for context and excluded from performance.">
        <table className="data">
          <thead>
            <tr>
              <th>Stage</th>
              <th className="num">Growth Engine cohort</th>
              <th className="num">Pre-launch (context)</th>
              <th className="num">Events</th>
            </tr>
          </thead>
          <tbody>
            {data.funnel.map((f) => (
              <tr key={f.level}>
                <td>L{f.level} · {f.label}</td>
                <td className="num" style={{ color: f.inCohort === 0 ? 'var(--text-faint)' : undefined }}>
                  {f.inCohort === 0 ? 'NO DATA' : f.inCohort}
                </td>
                <td className="num" style={{ color: 'var(--text-muted)' }}>{f.preLaunch}</td>
                <td className="num" style={{ color: 'var(--text-faint)' }}>{f.events}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {/* ---- Attribution: three models, none presented as the truth ---- */}
      <Panel
        title="Attribution by model"
        subtitle="Three models, none of which is 'the truth'. Paid acquisition is the default for advertising decisions and counts paid touchpoints only."
      >
        <div className="stack">
          {data.attribution.models.map((m) => (
            <div key={m.model}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <strong style={{ fontSize: 13 }}>{MODEL_LABEL[m.model] ?? m.model}</strong>
                {m.model === 'PAID_ACQUISITION' ? <span className="pill">default for advertising</span> : null}
              </div>
              <ConfidenceBar known={m.known} likely={m.likely} unknown={m.unknown} unattributed={m.unattributed} />
            </div>
          ))}
        </div>
        <p style={{ marginTop: 14, fontSize: 12, color: 'var(--text-faint)' }}>
          Paid acquisition shows no attribution because every touchpoint currently held is organic, direct,
          social or referral. Organic attribution is never counted as paid.
        </p>
      </Panel>

      {/* ---- Attribution health as a first-class widget ---- */}
      <Panel title="Attribution health" subtitle="A fall in attribution quality must be visible here, not silently change reported performance.">
        <div className="kpi-grid">
          <CountKpi label="Touchpoints" value={data.attributionHealth.touchpoints} />
          <CountKpi label="Paid touchpoints" value={data.attributionHealth.paidTouchpoints} />
          <CountKpi label="With click ID" value={data.attributionHealth.withClickId} note="KNOWN attribution requires one" />
          <CountKpi label="Missing click IDs" value={data.attributionHealth.missingClickIds} />
          <CountKpi label="Contacts without company" value={data.attributionHealth.contactsWithoutCompany} />
          <CountKpi label="Deals without contact" value={data.attributionHealth.dealsWithoutContact} note="Revenue cannot reach a keyword" />
          <CountKpi label="Open data-quality errors" value={data.attributionHealth.openErrors} />
          <CountKpi label="Open warnings" value={data.attributionHealth.openWarnings} />
        </div>
        <p style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
          Paid attribution coverage: <strong>{attributedPct}%</strong> of outcomes.
        </p>
      </Panel>

      {/* ---- Revenue by component ---- */}
      <Panel
        title="Won revenue by component"
        subtitle="Only approved managed-service revenue counts as MSP acquisition. A hardware quote is never equivalent to a managed-service agreement."
      >
        <table className="data">
          <thead>
            <tr><th>Component</th><th>Counts as MSP acquisition</th><th className="num">GE pipeline (won)</th></tr>
          </thead>
          <tbody>
            {data.revenue.components.map((c) => (
              <tr key={c.component_code}>
                <td>{c.display_name}</td>
                <td>{c.is_msp ? <span className="pill good">Yes</span> : <span className="pill">No</span>}</td>
                <td className="num" style={{ color: c.gbp === null ? 'var(--text-faint)' : undefined }}>
                  {c.gbp === null ? 'NO DATA' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(c.gbp)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.revenue.unclassifiedProducts > 0 ? (
          <p style={{ marginTop: 12, fontSize: 12, color: 'var(--warning)' }}>
            {data.revenue.unclassifiedProducts} products await revenue classification. Until classified they
            cannot contribute to managed-service MRR.
          </p>
        ) : null}
      </Panel>

      {/* ---- Historical context, explicitly separated ---- */}
      <Panel title="Historical pipeline" subtitle="Pre-launch context only. Excluded from every acquisition metric above.">
        <div style={{ marginBottom: 10 }}><CohortBadge cohort="PRE_LAUNCH" /></div>
        <table className="data">
          <thead><tr><th>Semantic stage</th><th className="num">Deals</th><th className="num">Value</th></tr></thead>
          <tbody>
            {data.pipeline.historical.map((h) => (
              <tr key={h.semantic_stage}>
                <td>{h.semantic_stage}</td>
                <td className="num">{h.n}</td>
                <td className="num" style={{ color: h.gbp === null ? 'var(--text-faint)' : undefined }}>
                  {h.gbp === null ? 'NO DATA' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(h.gbp)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
